import { useMsalToken } from "@/hooks/use-msal-token";
import { API_ENDPOINTS, TAG_BUILDER } from "./abstractions/api-types";
import type {
  ShiftDate,
  GetShiftDatesRequest,
  ShiftDateActionRequest,
  RejectShiftDateRequest,
  DeclareShiftDatePaymentRequest
} from "@/domain/shift-dates";
import type { VerificationDecisionRequest } from "@/domain/admin";
import { useAuthenticatedApi } from "./abstractions/use-authenticated-api";
import {
  DASHBOARD_QUERY_OPTIONS,
  DEFAULT_QUERY_OPTIONS,
  NOTIFICATION_QUERY_OPTIONS
} from "./abstractions/api-config";
import type {
  GetPracticeStatsRequest,
  GetPracticeStatsResponse,
  GetPracticeDashboardMobileRequest,
  GetPracticeDashboardMobileResponse,
  GetPracticeOpenShiftRequestsRequest,
  GetPracticeOpenShiftRequestsResponse,
  GetPracticeTierProgressRequest,
  GetPracticeTierProgressResponse,
  GetPracticePricingPlanSubscriptionRequest,
  GetPracticePricingPlanSubscriptionResponse,
  ChangePracticePricingPlanRequest,
  ChangePracticePricingPlanResponse,
  GetPracticeRequest,
  GetPracticeResponse,
  UpdatePracticeRequest,
  UpdatePracticeResponse,
  RegisterPracticeRequest,
  RegisterPracticeResponse,
  GetPracticeDocumentsResponse,
  GetPracticeDocumentsRequest,
  UploadPracticeDocumentResponse,
  UploadPracticeDocumentRequest,
  UpdatePracticeDocumentResponse,
  UpdatePracticeDocumentRequest,
  DeletePracticeDocumentResponse,
  DeletePracticeDocumentRequest,
  GetPracticeBranchesResponse,
  GetPracticeBranchesRequest,
  AddPracticeBranchResponse,
  AddPracticeBranchRequest,
  UpdatePracticeBranchResponse,
  UpdatePracticeBranchRequest,
  GetPracticeUsersResponse,
  GetPracticeUsersRequest,
  DeletePracticeBranchResponse,
  DeletePracticeBranchRequest,
  DownloadPracticeDocumentResponse,
  DownloadPracticeDocumentRequest,
  GetPracticeShiftsRequest,
  GetPracticeShiftsResponse,
  GetPracticeShiftResponse,
  GetPracticeShiftRequest,
  GetRecommendedShiftProfessionalsRequest,
  GetRecommendedShiftProfessionalsResponse,
  GetPracticeRecommendedProfessionalsFeedRequest,
  GetPracticeRecommendedProfessionalsFeedResponse,
  RecordRecommendationEventRequest,
  GetPracticeShiftStatusesResponse,
  GetPracticeShiftStatusesRequest,
  GetPracticeShiftLookupsResponse,
  GetPracticeShiftLookupsRequest,
  GetPracticeShiftTemplatesResponse,
  GetPracticeShiftTemplatesRequest,
  SaveShiftTemplateRequest,
  SaveShiftTemplateResponse,
  DeleteShiftTemplateRequest,
  UpdateShiftPostApiRequest,
  UpdateShiftPostApiResponse,
  UpdateShiftStatusRequest,
  UpdateShiftStatusResponse,
  CancelShiftPostsRequest,
  CancelShiftPostsResponse,
  SaveDraftShiftRequest,
  SaveDraftShiftResponse,
  AcceptShiftApplicationRequest,
  AcceptShiftApplicationResponse,
  RejectShiftApplicationRequest,
  RejectShiftApplicationResponse,
  NominateShiftProfessionalRequest,
  NominateShiftProfessionalResponse,
  RateProfessionalRequest,
  RateProfessionalResponse,
  GetPracticeRatingsRequest,
  GetPracticeRatingsResponse,
  PracticeSettingsResponse,
  UpdatePracticeSettingsRequest,
  SearchProfessionalsRequest,
  SearchProfessionalsResponse,
  GetFavouriteProfessionalsRequest,
  GetFavouriteProfessionalsResponse,
  AddProfessionalToFavouritesRequest,
  AddProfessionalToFavouritesResponse,
  RemoveProfessionalFromFavouritesRequest,
  RemoveProfessionalFromFavouritesResponse,
  GetPracticePaymentsOverviewRequest,
  GetPracticePaymentsOverviewResponse
} from "@/domain/practices";
import type {
  GetNotificationsRequest,
  GetNotificationsResponse
} from "@/domain/core";
import type {
  RaiseDisputeRequest,
  GetDisputesRequest,
  GetDisputesResponse,
  UploadPaymentEvidenceRequest,
  UploadPaymentEvidenceResponse,
  PracticeBillingProfile,
  StartBillingSetupResponse,
  CompleteBillingSetupRequest,
  PracticeBillingSetupResult,
  GetFeesRequest,
  GetFeesResponse,
  GetSubscriptionInvoicesRequest,
  GetSubscriptionInvoicesResponse
} from "@/domain/payments";
import {
  buildHttpDelete,
  buildHttpGet,
  buildHttpGetForDownload,
  buildHttpPost,
  buildHttpPut
} from "@/utilities/api-utils";
import { config } from "@/config/environment";
import type {
  CreateShiftRequest,
  CreateShiftResponse,
  ShiftStatus,
  UploadShiftDocumentRequest,
  UploadShiftDocumentResponse,
  RemoveShiftDocumentRequest,
  RemoveShiftDocumentResponse,
  UploadShiftTemplateDocumentRequest,
  UploadShiftTemplateDocumentResponse,
  RemoveShiftTemplateDocumentRequest,
  RemoveShiftTemplateDocumentResponse
} from "@/domain/shifts";
import { DEFAULT_PAGE_SIZE } from "@/constants";

// Authenticated Practices API hook
export function useAuthenticatedPracticesApi() {
  const authenticatedApi = useAuthenticatedApi();
  const { addAuthHeader } = useMsalToken();

  const practicesApi = authenticatedApi.injectEndpoints({
    overrideExisting: false,
    // Endpoints
    endpoints: (builder) => ({
      // Get practice statistics
      getPracticeStats: builder.query<
        GetPracticeStatsResponse,
        GetPracticeStatsRequest
      >({
        query: async ({ practiceId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.STATS(practiceId)
          ),
        providesTags: [TAG_BUILDER.ADMIN.REPORTS()],
        ...DASHBOARD_QUERY_OPTIONS
      }),

      getPracticeDashboardMobile: builder.query<
        GetPracticeDashboardMobileResponse,
        GetPracticeDashboardMobileRequest
      >({
        query: async ({ practiceId, weekStart }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.DASHBOARD_MOBILE(practiceId),
            weekStart ? { weekStart } : undefined
          ),
        providesTags: (_, __, { practiceId }) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ],
        ...DASHBOARD_QUERY_OPTIONS
      }),

      getPracticeOpenShiftRequests: builder.query<
        GetPracticeOpenShiftRequestsResponse,
        GetPracticeOpenShiftRequestsRequest
      >({
        query: async ({ practiceId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.OPEN_SHIFT_REQUESTS(practiceId)
          ),
        providesTags: (_, __, { practiceId }) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ],
        ...DASHBOARD_QUERY_OPTIONS
      }),

      getPracticeTierProgress: builder.query<
        GetPracticeTierProgressResponse,
        GetPracticeTierProgressRequest
      >({
        query: async ({ practiceId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.TIER_PROGRESS(practiceId)
          ),
        providesTags: (_, __, { practiceId }) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ],
        ...DASHBOARD_QUERY_OPTIONS
      }),

      getPracticePricingPlanSubscription: builder.query<
        GetPracticePricingPlanSubscriptionResponse,
        GetPracticePricingPlanSubscriptionRequest
      >({
        query: async ({ practiceId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.PRICING_PLAN_SUBSCRIPTION(practiceId)
          ),
        providesTags: (_, __, { practiceId }) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Schedule a plan change for the start of the next billing period
      changePracticePricingPlan: builder.mutation<
        ChangePracticePricingPlanResponse,
        ChangePracticePricingPlanRequest
      >({
        query: async ({ practiceId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.PRICING_PLAN_SUBSCRIPTION(practiceId),
            body
          ),
        invalidatesTags: (_, __, { practiceId }) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ]
      }),

      // --- Dated shift instances (the approval queue) ---------------------

      getPracticeShiftDates: builder.query<ShiftDate[], GetShiftDatesRequest>({
        query: async ({ ownerId, shiftId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.SHIFT_DATES(ownerId, shiftId)
          ),
        providesTags: (_, __, { ownerId }) => [TAG_BUILDER.PRACTICE.ENTITY(ownerId)],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Staff verification decision. Refreshes the practice detail query, which does
      // provide these tags. The admin list does not — it is fetched through a mutation,
      // so PracticesManager re-runs it explicitly after a decision lands.
      verifyPractice: builder.mutation<
        string,
        { practiceId: string } & VerificationDecisionRequest
      >({
        query: async ({ practiceId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.VERIFY(practiceId),
            body
          ),
        invalidatesTags: (_, __, { practiceId }) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId),
          TAG_BUILDER.PRACTICE.LIST(),
          TAG_BUILDER.ADMIN.REPORTS()
        ]
      }),

      approveShiftDate: builder.mutation<string, ShiftDateActionRequest>({
        query: async ({ ownerId, shiftDateId }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.APPROVE_SHIFT_DATE(ownerId, shiftDateId),
            {}
          ),
        invalidatesTags: (_, __, { ownerId }) => [TAG_BUILDER.PRACTICE.ENTITY(ownerId)]
      }),

      // One endpoint, two outcomes: the API records a no-show when the professional
      // never checked in and the window has closed, otherwise a rejected claim.
      rejectShiftDate: builder.mutation<string, RejectShiftDateRequest>({
        query: async ({ ownerId, shiftDateId, reason }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.REJECT_SHIFT_DATE(ownerId, shiftDateId),
            { reason }
          ),
        invalidatesTags: (_, __, { ownerId }) => [TAG_BUILDER.PRACTICE.ENTITY(ownerId)]
      }),

      declareShiftDatePayment: builder.mutation<string, DeclareShiftDatePaymentRequest>({
        query: async ({ practiceId, paymentId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.DECLARE_PAYMENT(practiceId, paymentId),
            body
          ),
        invalidatesTags: (_, __, { practiceId }) => [TAG_BUILDER.PRACTICE.ENTITY(practiceId)]
      }),

      // Attach proof of payment to one worked shift date
      uploadPaymentEvidence: builder.mutation<
        UploadPaymentEvidenceResponse,
        UploadPaymentEvidenceRequest
      >({
        query: async ({ practiceId, paymentId, formData }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.UPLOAD_PAYMENT_EVIDENCE(practiceId, paymentId),
            formData
          ),
        // The payments list carries the hasEvidence flag, and PAYMENTS() includes the
        // practice entity tag, so this refreshes the row the button sits on.
        invalidatesTags: (_, __, { practiceId }) => [TAG_BUILDER.PRACTICE.ENTITY(practiceId)]
      }),


      // Get specific practice
      getPractice: builder.query<GetPracticeResponse, GetPracticeRequest>({
        query: async (id) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.GET(id)
          ),
        providesTags: (_, __, id) => [TAG_BUILDER.PRACTICE.ENTITY(id)],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Update practice
      updatePractice: builder.mutation<
        UpdatePracticeResponse,
        UpdatePracticeRequest
      >({
        query: async (request) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.UPDATE(request.practiceId),
            request
          ),
        invalidatesTags: (_, __, { practiceId: id }) => [
          TAG_BUILDER.PRACTICE.ENTITY(id),
          TAG_BUILDER.PRACTICE.LIST(),
          TAG_BUILDER.ADMIN.REPORTS()
        ]
      }),

      // Get practice professionals


      // Register practice
      registerPractice: builder.mutation<
        RegisterPracticeResponse,
        RegisterPracticeRequest
      >({
        query: async (body) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.REGISTER,
            body
          ),
        invalidatesTags: (_, __, { userId }) => [
          TAG_BUILDER.USER.ENTITY(userId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get practice documents
      getPracticeDocuments: builder.query<
        GetPracticeDocumentsResponse,
        GetPracticeDocumentsRequest
      >({
        query: async ({ practiceId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.DOCUMENTS(practiceId)
          ),
        providesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.DOCUMENTS(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Upload single document i.e. tax file and license file
      uploadPracticeDocument: builder.mutation<
        UploadPracticeDocumentResponse,
        UploadPracticeDocumentRequest
      >({
        query: async ({ practiceId, formData }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.UPLOAD_DOCUMENT(practiceId),
            formData
          ),
        invalidatesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.DOCUMENTS(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Update a practice document based on document ID
      updatePracticeDocument: builder.mutation<
        UpdatePracticeDocumentResponse,
        UpdatePracticeDocumentRequest
      >({
        query: async ({ practiceId, documentId, description }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.DOCUMENT(practiceId, documentId),
            { description }
          ),
        invalidatesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.DOCUMENTS(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Delete a practice document base on document ID
      deletePracticeDocument: builder.mutation<
        DeletePracticeDocumentResponse,
        DeletePracticeDocumentRequest
      >({
        query: async ({ practiceId, documentId }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.DOCUMENT(practiceId, documentId)
          ),
        invalidatesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.DOCUMENTS(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get all branches for specific practice
      getPracticeBranches: builder.query<
        GetPracticeBranchesResponse,
        GetPracticeBranchesRequest
      >({
        query: async (practiceId) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.BRANCHES(practiceId)
          ),
        providesTags: (_, __, practiceId) =>
          TAG_BUILDER.PRACTICE.BRANCHES(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Add a new practice branch
      addPracticeBranch: builder.mutation<
        AddPracticeBranchResponse,
        AddPracticeBranchRequest
      >({
        query: async ({ practiceId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.BRANCHES(practiceId),
            body
          ),
        invalidatesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.BRANCHES(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Update an existing branch
      updatePracticeBranch: builder.mutation<
        UpdatePracticeBranchResponse,
        UpdatePracticeBranchRequest
      >({
        query: async ({ practiceId, branchId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.BRANCH(practiceId, branchId),
            body
          ),
        invalidatesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.BRANCHES(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Delete a specified branch
      deletePracticeBranch: builder.mutation<
        DeletePracticeBranchResponse,
        DeletePracticeBranchRequest
      >({
        query: async ({ practiceId, branchId }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.BRANCH(practiceId, branchId)
          ),
        invalidatesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.BRANCHES(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get all users of specific practice
      getPracticeUsers: builder.query<
        GetPracticeUsersResponse,
        GetPracticeUsersRequest
      >({
        query: async ({ practiceId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.USERS(practiceId)
          ),
        providesTags: (_, __, { practiceId }) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Downalod practice document
      downloadPracticeDocument: builder.query<
        DownloadPracticeDocumentResponse,
        DownloadPracticeDocumentRequest
      >({
        query: async ({ practiceId, documentId }) =>
          await buildHttpGetForDownload(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.DOWNLOAD_DOCUMENT(practiceId, documentId)
          ),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get practice shifts statuses
      getPracticeShiftsStatuses: builder.query<
        GetPracticeShiftStatusesResponse,
        GetPracticeShiftStatusesRequest
      >({
        query: async ({
          practiceId,
          practiceBranchId
        }: GetPracticeShiftStatusesRequest) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.SHIFTS.STATUSES_COUNT(practiceId),
            { practiceBranchId }
          ),
        providesTags: (result?: GetPracticeShiftStatusesResponse) =>
          result
            ? [
              ...result.statuses.map(({ status }: { status: ShiftStatus }) =>
                TAG_BUILDER.SHIFT.ENTITY(status)
              ),
              TAG_BUILDER.SHIFT.STATUSES_COUNT()
            ]
            : [TAG_BUILDER.SHIFT.STATUSES_COUNT()],
        ...DASHBOARD_QUERY_OPTIONS
      }),

      // Get all practice shifts
      getPracticeShifts: builder.query<
        GetPracticeShiftsResponse,
        GetPracticeShiftsRequest
      >({
        query: async ({ practiceId, ...queryParams }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.SHIFTS(practiceId),
            queryParams
          ),
        providesTags: (result) =>
          result
            ? [
              ...result.items.map(({ id }) => TAG_BUILDER.SHIFT.ENTITY(id)),
              TAG_BUILDER.SHIFT.LIST()
            ]
            : [TAG_BUILDER.SHIFT.LIST()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // get practice shift by shift id (get specific shift)
      getPracticeShift: builder.query<
        GetPracticeShiftResponse,
        GetPracticeShiftRequest
      >({
        query: async ({ practiceId, shiftId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.SHIFT(practiceId, shiftId)
          ),
        providesTags: (_, __, { shiftId }) => [
          TAG_BUILDER.SHIFT.ENTITY(shiftId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get recommended professionals for a shift (AI match feed)
      getRecommendedShiftProfessionals: builder.query<
        GetRecommendedShiftProfessionalsResponse,
        GetRecommendedShiftProfessionalsRequest
      >({
        query: async ({ practiceId, shiftId, limit = 12 }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.RECOMMENDED_SHIFT_PROFESSIONALS(
              practiceId,
              shiftId
            ),
            { limit }
          ),
        providesTags: (_, __, { shiftId }) => [
          TAG_BUILDER.SHIFT.ENTITY(shiftId),
          TAG_BUILDER.PROFESSIONAL.LIST()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Paginated feed: top matched professionals per open shift (same scoring as per-shift AI match)
      getPracticeRecommendedProfessionalsFeed: builder.query<
        GetPracticeRecommendedProfessionalsFeedResponse,
        GetPracticeRecommendedProfessionalsFeedRequest
      >({
        query: async ({
          practiceId,
          page = 1,
          pageSize = 4,
          professionalsPerShift = 4
        }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.RECOMMENDED_PROFESSIONALS_FEED(practiceId),
            { page, pageSize, professionalsPerShift }
          ),
        providesTags: (_, __, { practiceId }) => [
          TAG_BUILDER.SHIFT.LIST(),
          TAG_BUILDER.PROFESSIONAL.LIST(),
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      recordRecommendationEvent: builder.mutation<
        void,
        RecordRecommendationEventRequest
      >({
        query: async ({ practiceId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.RECOMMENDATION_EVENTS(practiceId),
            body
          ),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get practice shift lookups (branches, specialties, skills, market rates)
      getPracticeShiftLookups: builder.query<
        GetPracticeShiftLookupsResponse,
        GetPracticeShiftLookupsRequest
      >({
        query: async ({ practiceId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.SHIFT_LOOKUPS(practiceId)
          ),
        providesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.SHIFT_LOOKUPS(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get practice shift templates (saved templates)
      getPracticeShiftTemplates: builder.query<
        GetPracticeShiftTemplatesResponse,
        GetPracticeShiftTemplatesRequest
      >({
        query: async ({ practiceId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.SHIFT_TEMPLATES(practiceId)
          ),
        providesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.SHIFT_TEMPLATES(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // create a new shift (Post a new shift)
      createPracticeShift: builder.mutation<
        CreateShiftResponse,
        CreateShiftRequest
      >({
        query: async ({ practiceId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.SHIFTS(practiceId),
            body
          ),
        invalidatesTags: [
          TAG_BUILDER.SHIFT.LIST(),
          TAG_BUILDER.ADMIN.REPORTS()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Save shift template
      saveShiftTemplate: builder.mutation<
        SaveShiftTemplateResponse,
        SaveShiftTemplateRequest
      >({
        query: async ({ practiceId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.SAVE_SHIFT_TEMPLATE(practiceId),
            body
          ),
        invalidatesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.SHIFT_TEMPLATES(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Delete shift template
      deleteShiftTemplate: builder.mutation<void, DeleteShiftTemplateRequest>({
        query: async ({ practiceId, templateId }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.DELETE_SHIFT_TEMPLATE(
              practiceId,
              templateId
            )
          ),
        invalidatesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.SHIFT_TEMPLATES(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Update shift post
      updateShiftPost: builder.mutation<
        UpdateShiftPostApiResponse,
        UpdateShiftPostApiRequest
      >({
        query: async ({
          practiceId,
          shiftId,
          shiftPostId,
          ...body
        }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.UPDATE_SHIFT_POST(
              practiceId,
              shiftId,
              shiftPostId
            ),
            body
          ),
        // The detail page reads the post's date, slots, hours and value, all of which
        // this mutation rewrites.
        invalidatesTags: (_result, _error, { shiftId }) => [
          TAG_BUILDER.SHIFT.LIST(),
          TAG_BUILDER.SHIFT.ENTITY(shiftId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Update shift status (publish / unpublish)
      updateShiftStatus: builder.mutation<
        UpdateShiftStatusResponse,
        UpdateShiftStatusRequest
      >({
        query: async ({ practiceId, shiftId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.UPDATE_SHIFT_STATUS(practiceId, shiftId),
            body
          ),
        invalidatesTags: (_, __, { shiftId }) => [
          TAG_BUILDER.SHIFT.ENTITY(shiftId),
          TAG_BUILDER.SHIFT.LIST(),
          TAG_BUILDER.SHIFT.STATUSES_COUNT()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Cancel shift posts
      cancelShiftPosts: builder.mutation<
        CancelShiftPostsResponse,
        CancelShiftPostsRequest
      >({
        query: async ({ practiceId, shiftId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.CANCEL_SHIFT(practiceId, shiftId),
            body
          ),
        invalidatesTags: [
          TAG_BUILDER.SHIFT.LIST(),
          TAG_BUILDER.SHIFT.STATUSES_COUNT()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Save draft shift
      saveDraftShift: builder.mutation<
        SaveDraftShiftResponse,
        SaveDraftShiftRequest
      >({
        query: async ({ practiceId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.SAVE_DRAFT_SHIFT(practiceId),
            body
          ),
        invalidatesTags: [
          TAG_BUILDER.SHIFT.LIST(),
          TAG_BUILDER.SHIFT.STATUSES_COUNT()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Accept shift application
      acceptShiftApplication: builder.mutation<
        AcceptShiftApplicationResponse,
        AcceptShiftApplicationRequest
      >({
        query: async ({ practiceId, shiftId, applicationId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.ACCEPT_APPLICATION(
              practiceId,
              shiftId,
              applicationId
            ),
            body
          ),
        invalidatesTags: (_, __, { shiftId }) => [
          TAG_BUILDER.SHIFT.ENTITY(shiftId),
          TAG_BUILDER.SHIFT.LIST(),
          TAG_BUILDER.SHIFT.STATUSES_COUNT()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Reject shift application
      rejectShiftApplication: builder.mutation<
        RejectShiftApplicationResponse,
        RejectShiftApplicationRequest
      >({
        query: async ({ practiceId, shiftId, applicationId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.REJECT_APPLICATION(
              practiceId,
              shiftId,
              applicationId
            ),
            body
          ),
        invalidatesTags: (_, __, { shiftId }) => [
          TAG_BUILDER.SHIFT.ENTITY(shiftId),
          TAG_BUILDER.SHIFT.LIST(),
          TAG_BUILDER.SHIFT.STATUSES_COUNT()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Nominate a professional for a shift
      nominateShiftProfessional: builder.mutation<
        NominateShiftProfessionalResponse,
        NominateShiftProfessionalRequest
      >({
        query: async ({ practiceId, shiftId, professionalId, notes }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.NOMINATE_PROFESSIONAL(
              practiceId,
              shiftId
            ),
            { professionalId, notes }
          ),
        invalidatesTags: (_, __, { shiftId }) => [
          TAG_BUILDER.SHIFT.ENTITY(shiftId),
          TAG_BUILDER.SHIFT.LIST()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Rate a professional
      rateProfessional: builder.mutation<
        RateProfessionalResponse,
        RateProfessionalRequest
      >({
        query: async ({ practiceId, professionalId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.RATE_PROFESSIONAL(practiceId, professionalId),
            body
          ),
        invalidatesTags: (_, __, { practiceId }) => [
          ...TAG_BUILDER.PRACTICE.REVIEWS(practiceId),
          TAG_BUILDER.REVIEW.LIST(),
          // The hired tab reads canRate off the applications list, which is tagged as a
          // shift list. Without this the Rate button it just used stays on screen and
          // 409s on a second press. The professional's side needs no equivalent: its
          // opportunity query is tagged with the professional entity, which
          // PROFESSIONAL.REVIEWS already carries.
          TAG_BUILDER.SHIFT.LIST()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get practice ratings
      getPracticeRatings: builder.query<
        GetPracticeRatingsResponse,
        GetPracticeRatingsRequest
      >({
        query: async ({ practiceId, page = 1, pageSize = DEFAULT_PAGE_SIZE }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.RATINGS(practiceId),
            { page, pageSize }
          ),
        providesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.REVIEWS(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Search professionals
      searchProfessionals: builder.query<
        SearchProfessionalsResponse,
        SearchProfessionalsRequest
      >({
        query: async ({ practiceId, ...queryParams }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.SEARCH_PROFESSIONALS(practiceId),
            queryParams
          ),
        providesTags: [TAG_BUILDER.PROFESSIONAL.LIST()],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get favourite professionals for filter dropdown
      getFavouriteProfessionals: builder.query<
        GetFavouriteProfessionalsResponse,
        GetFavouriteProfessionalsRequest
      >({
        query: async (practiceId) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.FAVOURITE_PROFESSIONALS(practiceId)
          ),
        providesTags: (_, __, practiceId) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Add professional to favourites
      addProfessionalToFavourites: builder.mutation<
        AddProfessionalToFavouritesResponse,
        AddProfessionalToFavouritesRequest
      >({
        query: async ({ practiceId, professionalId }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.ADD_PROFESSIONAL_TO_FAVOURITES(practiceId, professionalId),
            {}
          ),
        invalidatesTags: (_, __, { practiceId }) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Remove professional from favourites
      removeProfessionalFromFavourites: builder.mutation<
        RemoveProfessionalFromFavouritesResponse,
        RemoveProfessionalFromFavouritesRequest
      >({
        query: async ({ practiceId, professionalId }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.REMOVE_PROFESSIONAL_FROM_FAVOURITES(practiceId, professionalId)
          ),
        invalidatesTags: (_, __, { practiceId }) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Upload shift document
      uploadShiftDocument: builder.mutation<
        UploadShiftDocumentResponse,
        UploadShiftDocumentRequest
      >({
        query: async ({ practiceId, shiftId, formData }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.UPLOAD_SHIFT_DOCUMENT(practiceId, shiftId),
            formData
          ),
        invalidatesTags: (_, __, { shiftId }) => [
          TAG_BUILDER.SHIFT.ENTITY(shiftId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Remove shift document
      removeShiftDocument: builder.mutation<
        RemoveShiftDocumentResponse,
        RemoveShiftDocumentRequest
      >({
        query: async ({ practiceId, shiftId, documentId }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.REMOVE_SHIFT_DOCUMENT(
              practiceId,
              shiftId,
              documentId
            )
          ),
        invalidatesTags: (_, __, { shiftId }) => [
          TAG_BUILDER.SHIFT.ENTITY(shiftId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Upload shift template document
      uploadShiftTemplateDocument: builder.mutation<
        UploadShiftTemplateDocumentResponse,
        UploadShiftTemplateDocumentRequest
      >({
        query: async ({ practiceId, templateId, formData }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.UPLOAD_SHIFT_TEMPLATE_DOCUMENT(
              practiceId,
              templateId
            ),
            formData
          ),
        invalidatesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.SHIFT_TEMPLATES(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Remove shift template document
      removeShiftTemplateDocument: builder.mutation<
        RemoveShiftTemplateDocumentResponse,
        RemoveShiftTemplateDocumentRequest
      >({
        query: async ({ practiceId, templateId, documentId }) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.REMOVE_SHIFT_TEMPLATE_DOCUMENT(
              practiceId,
              templateId,
              documentId
            )
          ),
        invalidatesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.SHIFT_TEMPLATES(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get practice settings
      getPracticeSettings: builder.query<
        PracticeSettingsResponse,
        { practiceId: string }
      >({
        query: async ({ practiceId }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.SETTINGS(practiceId)
          ),
        providesTags: (_, __, { practiceId }) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Update practice settings
      updatePracticeSettings: builder.mutation<
        string,
        UpdatePracticeSettingsRequest
      >({
        query: async ({ practiceId, ...body }) =>
          await buildHttpPut(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.SETTINGS(practiceId),
            body
          ),
        invalidatesTags: (_, __, { practiceId }) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Raise a dispute
      raisePracticeDispute: builder.mutation<
        string,
        RaiseDisputeRequest & { practiceId: string }
      >({
        query: async ({ practiceId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.RAISE_DISPUTE(practiceId),
            body
          ),
        invalidatesTags: (_, __, { practiceId }) => [
          ...TAG_BUILDER.PRACTICE.PAYMENTS(practiceId),
          TAG_BUILDER.PAYMENT.LIST()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get practice payments overview (stats + paginated payment records)
      getPracticePaymentsOverview: builder.query<
        GetPracticePaymentsOverviewResponse,
        GetPracticePaymentsOverviewRequest
      >({
        query: async ({
          practiceId,
          status,
          page = 1,
          pageSize = DEFAULT_PAGE_SIZE,
          sortBy,
          sortOrder = "desc",
          searchTerm
        }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.PAYMENTS_OVERVIEW(practiceId),
            { status, page, pageSize, sortBy, sortOrder, searchTerm }
          ),
        providesTags: (_, __, { practiceId }) => [
          ...TAG_BUILDER.PRACTICE.PAYMENTS(practiceId),
          TAG_BUILDER.PAYMENT.LIST()
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // The practice's billing identity: gateway state + stored card
      getPracticeBillingProfile: builder.query<PracticeBillingProfile, string>({
        query: async (practiceId) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.BILLING(practiceId)
          ),
        providesTags: (_, __, practiceId) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ],
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Mint a SetupIntent for the card form. Not cached: each open of the
      // dialog needs a fresh secret.
      startBillingSetup: builder.mutation<StartBillingSetupResponse, string>({
        query: async (practiceId) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.BILLING_SETUP_INTENT(practiceId),
            {}
          )
      }),

      // Record the card a confirmed SetupIntent stored
      completeBillingSetup: builder.mutation<
        PracticeBillingSetupResult,
        CompleteBillingSetupRequest
      >({
        query: async ({ practiceId, setupIntentId }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.BILLING_PAYMENT_METHOD(practiceId),
            { setupIntentId }
          ),
        invalidatesTags: (_, __, { practiceId }) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ]
      }),

      // Remove the stored card. Fees keep accruing as due.
      removeBillingPaymentMethod: builder.mutation<void, string>({
        query: async (practiceId) =>
          await buildHttpDelete(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.BILLING_PAYMENT_METHOD(practiceId)
          ),
        invalidatesTags: (_, __, practiceId) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ]
      }),

      // Tempii's own shift fees against this practice
      getPracticeFees: builder.query<
        GetFeesResponse,
        GetFeesRequest & { practiceId: string }
      >({
        query: async ({ practiceId, status, page = 1, pageSize = DEFAULT_PAGE_SIZE }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.FEES(practiceId),
            { status: status || undefined, page, pageSize }
          ),
        providesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.PAYMENTS(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Billing history: one row per invoiced plan period, at the price in
      // force when the period was billed
      getPracticeSubscriptionInvoices: builder.query<
        GetSubscriptionInvoicesResponse,
        GetSubscriptionInvoicesRequest & { practiceId: string }
      >({
        query: async ({ practiceId, status, page = 1, pageSize = DEFAULT_PAGE_SIZE }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.SUBSCRIPTION_INVOICES(practiceId),
            { status: status || undefined, page, pageSize }
          ),
        providesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.PAYMENTS(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Raise a dispute against a payment record (professional denies receiving it)
      raiseDispute: builder.mutation<
        string,
        RaiseDisputeRequest & { practiceId: string }
      >({
        query: async ({ practiceId, ...body }) =>
          await buildHttpPost(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.RAISE_DISPUTE(practiceId),
            body
          ),
        // The payments overview and the disputes list both key off the practice
        // entity tag, so one invalidation refreshes the row and the panel.
        invalidatesTags: (_, __, { practiceId }) => [
          TAG_BUILDER.PRACTICE.ENTITY(practiceId)
        ]
      }),

      // Get practice disputes
      getPracticeDisputes: builder.query<
        GetDisputesResponse,
        GetDisputesRequest & { practiceId: string }
      >({
        query: async ({ practiceId, page = 1, pageSize = DEFAULT_PAGE_SIZE }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.DISPUTES(practiceId),
            { page, pageSize }
          ),
        providesTags: (_, __, { practiceId }) =>
          TAG_BUILDER.PRACTICE.PAYMENTS(practiceId),
        ...DEFAULT_QUERY_OPTIONS
      }),

      // Get practice notifications
      getPracticeNotifications: builder.query<
        GetNotificationsResponse,
        GetNotificationsRequest
      >({
        query: async ({
          entityId,
          page = 1,
          pageSize = DEFAULT_PAGE_SIZE,
          isRead,
          searchTerm,
          sortBy,
          sortOrder
        }) =>
          await buildHttpGet(
            config,
            addAuthHeader,
            API_ENDPOINTS.PRACTICES.NOTIFICATIONS(entityId),
            { page, pageSize, isRead, searchTerm, sortBy, sortOrder }
          ),
        providesTags: (_, __, { entityId }) => [
          TAG_BUILDER.NOTIFICATION.FOR_ENTITY(entityId)
        ],
        ...NOTIFICATION_QUERY_OPTIONS
      })
    })
  });

  return {
    useGetPracticeStatsQuery: practicesApi.useGetPracticeStatsQuery,
    useGetPracticeDashboardMobileQuery:
      practicesApi.useGetPracticeDashboardMobileQuery,
    useGetPracticeOpenShiftRequestsQuery:
      practicesApi.useGetPracticeOpenShiftRequestsQuery,
    useGetPracticeTierProgressQuery: practicesApi.useGetPracticeTierProgressQuery,
    useGetPracticePricingPlanSubscriptionQuery: practicesApi.useGetPracticePricingPlanSubscriptionQuery,
    useChangePracticePricingPlanMutation: practicesApi.useChangePracticePricingPlanMutation,
    useGetPracticeShiftDatesQuery: practicesApi.useGetPracticeShiftDatesQuery,
    useVerifyPracticeMutation: practicesApi.useVerifyPracticeMutation,
    useApproveShiftDateMutation: practicesApi.useApproveShiftDateMutation,
    useRejectShiftDateMutation: practicesApi.useRejectShiftDateMutation,
    useDeclareShiftDatePaymentMutation: practicesApi.useDeclareShiftDatePaymentMutation,
    useUploadPaymentEvidenceMutation: practicesApi.useUploadPaymentEvidenceMutation,
    useGetPracticeBillingProfileQuery: practicesApi.useGetPracticeBillingProfileQuery,
    useStartBillingSetupMutation: practicesApi.useStartBillingSetupMutation,
    useCompleteBillingSetupMutation: practicesApi.useCompleteBillingSetupMutation,
    useRemoveBillingPaymentMethodMutation:
      practicesApi.useRemoveBillingPaymentMethodMutation,
    useGetPracticeFeesQuery: practicesApi.useGetPracticeFeesQuery,
    useGetPracticeSubscriptionInvoicesQuery:
      practicesApi.useGetPracticeSubscriptionInvoicesQuery,
    useGetPracticeQuery: practicesApi.useGetPracticeQuery,
    useUpdatePracticeMutation: practicesApi.useUpdatePracticeMutation,
    useRegisterPracticeMutation: practicesApi.useRegisterPracticeMutation,
    useGetPracticeDocumentsQuery: practicesApi.useGetPracticeDocumentsQuery,
    useUploadPracticeDocumentMutation:
      practicesApi.useUploadPracticeDocumentMutation,
    useUpdatePracticeDocumentMutation:
      practicesApi.useUpdatePracticeDocumentMutation,
    useDeletePracticeDocumentMutation:
      practicesApi.useDeletePracticeDocumentMutation,
    useGetPracticeBranchesQuery: practicesApi.useGetPracticeBranchesQuery,
    useAddPracticeBranchMutation: practicesApi.useAddPracticeBranchMutation,
    useUpdatePracticeBranchMutation:
      practicesApi.useUpdatePracticeBranchMutation,
    useGetPracticeUsersQuery: practicesApi.useGetPracticeUsersQuery,
    useDeletePracticeBranchMutation:
      practicesApi.useDeletePracticeBranchMutation,
    useLazyDownloadPracticeDocumentQuery:
      practicesApi.useLazyDownloadPracticeDocumentQuery,
    useGetPracticeShiftsStatusesQuery:
      practicesApi.useGetPracticeShiftsStatusesQuery,
    useGetPracticeShiftsQuery: practicesApi.useGetPracticeShiftsQuery,
    useGetPracticeShiftQuery: practicesApi.useGetPracticeShiftQuery,
    useGetRecommendedShiftProfessionalsQuery:
      practicesApi.useGetRecommendedShiftProfessionalsQuery,
    useGetPracticeRecommendedProfessionalsFeedQuery:
      practicesApi.useGetPracticeRecommendedProfessionalsFeedQuery,
    useRecordRecommendationEventMutation:
      practicesApi.useRecordRecommendationEventMutation,
    useGetPracticeShiftLookupsQuery:
      practicesApi.useGetPracticeShiftLookupsQuery,
    useGetPracticeShiftTemplatesQuery:
      practicesApi.useGetPracticeShiftTemplatesQuery,
    useCreatePracticeShiftMutation: practicesApi.useCreatePracticeShiftMutation,
    useSaveShiftTemplateMutation: practicesApi.useSaveShiftTemplateMutation,
    useDeleteShiftTemplateMutation: practicesApi.useDeleteShiftTemplateMutation,
    useUpdateShiftPostMutation: practicesApi.useUpdateShiftPostMutation,
    useUpdateShiftStatusMutation: practicesApi.useUpdateShiftStatusMutation,
    useCancelShiftPostsMutation: practicesApi.useCancelShiftPostsMutation,
    useSaveDraftShiftMutation: practicesApi.useSaveDraftShiftMutation,
    useAcceptShiftApplicationMutation:
      practicesApi.useAcceptShiftApplicationMutation,
    useRejectShiftApplicationMutation:
      practicesApi.useRejectShiftApplicationMutation,
    useNominateShiftProfessionalMutation:
      practicesApi.useNominateShiftProfessionalMutation,
    useRateProfessionalMutation: practicesApi.useRateProfessionalMutation,
    useGetPracticeRatingsQuery: practicesApi.useGetPracticeRatingsQuery,
    useGetPracticeNotificationsQuery:
      practicesApi.useGetPracticeNotificationsQuery,
    useRaisePracticeDisputeMutation:
      practicesApi.useRaisePracticeDisputeMutation,
    useGetPracticeDisputesQuery: practicesApi.useGetPracticeDisputesQuery,
    useRaiseDisputeMutation: practicesApi.useRaiseDisputeMutation,
    useGetPracticePaymentsOverviewQuery:
      practicesApi.useGetPracticePaymentsOverviewQuery,
    useGetPracticeSettingsQuery: practicesApi.useGetPracticeSettingsQuery,
    useUpdatePracticeSettingsMutation:
      practicesApi.useUpdatePracticeSettingsMutation,
    useSearchProfessionalsQuery: practicesApi.useSearchProfessionalsQuery,
    // Favourites hooks
    useGetFavouriteProfessionalsQuery:
      practicesApi.useGetFavouriteProfessionalsQuery,
    useAddProfessionalToFavouritesMutation:
      practicesApi.useAddProfessionalToFavouritesMutation,
    useRemoveProfessionalFromFavouritesMutation:
      practicesApi.useRemoveProfessionalFromFavouritesMutation,
    // Shift document hooks
    useUploadShiftDocumentMutation:
      practicesApi.useUploadShiftDocumentMutation,
    useRemoveShiftDocumentMutation:
      practicesApi.useRemoveShiftDocumentMutation,

    // Shift template document hooks
    useUploadShiftTemplateDocumentMutation:
      practicesApi.useUploadShiftTemplateDocumentMutation,
    useRemoveShiftTemplateDocumentMutation:
      practicesApi.useRemoveShiftTemplateDocumentMutation
  };
}
